import { StateCreator } from 'zustand';
import { BoundStoreState, useBoundStore } from './index';

export type EntityType = 'block' | 'article' | 'section';
export type FieldType = 'title' | 'body';

export interface HistoryEntry {
	id: number;
	timestamp: string;
	title: string;
	body?: string;
	user: string;
}

export interface EntityHistoryResponse {
	entity_type: EntityType;
	entity_id: number;
	history: HistoryEntry[];
}

export interface EntityHistorySliceState {
	entityHistoryDialog: {
		open: boolean;
		loading: boolean;
		error: string | null;
		data: EntityHistoryResponse | null;
		entityType: EntityType | null;
		entityId: number | null;
		fieldType: FieldType;
	};
	appliedVersion: {
		fieldType: FieldType | null;
		content: string | null;
		timestamp: number | null;
	};
	editLanguage: string;
}

export const createEntityHistorySlice: StateCreator<
		BoundStoreState,
		[],
		[],
		EntityHistorySliceState
> = () => ({
  entityHistoryDialog: {
    open: false,
    loading: false,
    error: null,
    data: null,
    entityType: null,
    entityId: null,
    fieldType: 'title',
  },
  appliedVersion: {
    fieldType: null,
    content: null,
    timestamp: null,
  },
  editLanguage: 'nl',
});

export const openEntityHistoryDialog = (
  entityType: EntityType,
  entityId: number,
  fieldType: FieldType = 'title',
) => {
  useBoundStore.setState({
    entityHistoryDialog: {
      open: true,
      loading: false,
      error: null,
      data: null,
      entityType,
      entityId,
      fieldType,
    },
  });
};


export const closeEntityHistoryDialog = () => {
  useBoundStore.setState((state) => ({
    entityHistoryDialog: {
      ...state.entityHistoryDialog,
      open: false,
      data: null,
    },
  }));
};

export const setEntityHistoryLoading = (loading: boolean) => {
  useBoundStore.setState((state) => ({
    entityHistoryDialog: {
      ...state.entityHistoryDialog,
      loading,
      error: loading ? null : state.entityHistoryDialog.error,
    },
  }));
};

export const setEntityHistoryData = (data: EntityHistoryResponse) => {
  useBoundStore.setState((state) => ({
    entityHistoryDialog: {
      ...state.entityHistoryDialog,
      data,
      loading: false,
      error: null,
    },
  }));
};

export const setEntityHistoryError = (error: string) => {
  useBoundStore.setState((state) => ({
    entityHistoryDialog: {
      ...state.entityHistoryDialog,
      error,
      loading: false,
    },
  }));
};

export const applyHistoryVersion = (fieldType: FieldType, content: string) => {
  useBoundStore.setState({
    appliedVersion: {
      fieldType,
      content,
      timestamp: Date.now(),
    },
  });
};

export const clearAppliedVersion = () => {
  useBoundStore.setState({
    appliedVersion: {
      fieldType: null,
      content: null,
      timestamp: null,
    },
  });
};

export const useEntityHistoryDialog = () =>
  useBoundStore((state) => state.entityHistoryDialog);

export const useEntityHistoryDialogOpen = () =>
  useBoundStore((state) => state.entityHistoryDialog.open);

export const useEntityHistoryDialogData = () =>
  useBoundStore((state) => state.entityHistoryDialog.data);

export const useEntityHistoryDialogLoading = () =>
  useBoundStore((state) => state.entityHistoryDialog.loading);

export const useEntityHistoryDialogError = () =>
  useBoundStore((state) => state.entityHistoryDialog.error);

export const useEntityHistoryDialogFieldType = () =>
  useBoundStore((state) => state.entityHistoryDialog.fieldType);

export const useAppliedVersion = () =>
  useBoundStore((state) => state.appliedVersion);

export const setEditLanguage = (language: string) => {
  console.log('Setting edit language to:', language);
  useBoundStore.setState({ editLanguage: language });
};

export const useEditLanguage = () =>
  useBoundStore((state) => state.editLanguage);